/**
 * Reservations-calendar domain color pairs (DNKADM-0271 §4 dark tokenization). The
 * calendar dir (`src/features/reservations/calendar/**`) was DNKADM-0269's one
 * deliberate carve-out — the redesign in this spec rewrites it, so tokenization lands
 * here instead. LIGHT values are byte-identical to the pre-0271 literals (the 0266
 * identity constraint); DARK follows the established translucent-tint + lifted-ink
 * ramp (mirrors `STATUS_DARK` / the dark `AdminTokens` in `../tokens.ts` and
 * `../theme.ts`).
 *
 * Consumer pattern: `const cm = resolveCalendarMaps(useTheme())` for components: the
 * pure (non-component) helpers in `derive.ts` / `contrastPolicy.ts` instead take a
 * trailing `mode: 'light' | 'dark' = 'light'` param and call `buildCalendarMaps(mode)`
 * directly, so a caller with no mounted theme (a unit test) still gets the light
 * (pre-0271) values by default.
 *
 * **Surface alignment (DNKADM-0297 U3).** The 0271 dark ramp built its
 * surface/border/text values by COPYING the dark `AdminTokens` — when 0297
 * moved the token ladder, those copies silently desynced. Fields that were
 * copies now REFERENCE `buildTokens` (`LT`/`DT` below) so the next palette
 * change cannot desync them again. Fields that only LOOKED like copies are
 * deliberately independent and stay literal, each with a comment saying why:
 * the DNKADM-0181 contrast-policy values (`cancelled*Text`, `heatmap*Fg`, and
 * `cancelledFill` — the fill the approved cancelled ink was rated against),
 * ink-on-colored-fill values (`todayChipInk`/`headerInk` — not surfaces), and
 * the status-pill family (U3's fg-never-changes rule). Light literals that
 * never matched a token (the calendar's own blue-grey text family, its grid
 * hairlines) are untouched.
 */
import type { Theme } from '@mui/material/styles';
import { buildTokens } from '../theme';

// The app surface ladders the bound fields below read from. Resolved once at
// module load — same lifetime as the const maps they feed.
const LT = buildTokens('light');
const DT = buildTokens('dark');

/** A service color pair: the strong `main` (border/accent) + the soft `bg` (fill). */
export interface ServiceColor {
  main: string;
  bg: string;
}

/** One status-pill ink/fill pair (Day/Week List + Day/Week grid status pills). */
interface PillPair {
  fg: string;
  bg: string;
}

export interface CalendarMaps {
  /** The design's six-color service palette — hashed by name (`deriveServiceColor`). */
  servicePalette: readonly ServiceColor[];
  /** The neutral fallback for an unknown/blank service name. */
  neutralService: ServiceColor;

  /** The "now" line drawn across today's column/row. */
  nowLine: string;
  /** Hour/day grid-line color (Timeline/Columns/Schedule tracks). */
  gridLine: string;
  /** A cancelled block's dimmed fill (fed through `dimDecorativeColor`). */
  cancelledFill: string;
  /** The light off-hours band (before open / after close) on the Day grids. */
  offHoursFill: string;
  /** Today's row/column tint (Week Schedule + List day header). */
  todayTint: string;
  /** Today's Week Schedule day-track wash (subtler than `todayTint`). */
  todayTrackTint: string;
  /** Today's accent ink (labels, the TODAY chip, heatmap headers). */
  todayAccent: string;
  /** Ink ON the TODAY chip's `todayAccent` fill. */
  todayChipInk: string;
  /**
   * Ink on a solid service-color fill (the drawer's colored header). Light mains are
   * vivid enough for white ink; dark mains are lifted pastels (DNKADM-0271 §4) that
   * need a dark ink instead — same value as `todayChipInk`, named separately for
   * call-site clarity.
   */
  headerInk: string;

  /** The Day/Week List + grid status pill ink/fill pairs. */
  statusPill: Record<'confirmed' | 'pending' | 'cancelled' | 'neutral', PillPair>;

  /**
   * Payment-state ink/fill pairs (DNKADM-0350 U2/U3 — the Day-grid block badge
   * + the drawer banner). A DELIBERATELY SEPARATE field from `statusPill`
   * (booking status, a different domain) even though the color FAMILIES are
   * shared on purpose (the spec: "reuse if it fits, don't fork a second
   * palette") — `paid`/`due`/`comp` reuse the exact `confirmed`/`pending`/
   * `neutral` pairs' values so a reader never wonders whether a booking-status
   * pill and a payment-state chip disagree about what "green" means.
   * `refunded` (Operator ruling, post-ship) reuses the SAME `neutral` pair as
   * `comp` verbatim — "same visual weight as Comp so nobody chases it" — but
   * is its own distinct rendered state (a DISTINCT text token, "Refunded" ≠
   * "Comp"; see `derive.ts` `derivePaymentState`).
   */
  paymentState: Record<
    'paid' | 'due' | 'comp' | 'refunded' | 'unpaid' | 'in-progress' | 'needs-review',
    PillPair
  >;

  /** The Timeline/Columns cancelled-block label ink (DNKADM-0181 approved contrast). */
  cancelledBlockText: string;
  /** The Day/Week List cancelled-row label ink (DNKADM-0181 approved contrast). */
  cancelledListText: string;

  /** Heatmap cell foreground below the low-utilization boundary. */
  heatmapLowFg: string;
  /** Heatmap cell foreground in the mid-utilization band. */
  heatmapMidFg: string;
  /** Heatmap cell foreground at/above the high-utilization boundary. */
  heatmapHighFg: string;
  /** The heatmap cell background's `r, g, b` triple (composited at a utilization alpha). */
  heatmapBase: string;

  /** The grid/card/table surface (Day/Week grid containers, List tables). */
  surface: string;
  /** The Day List's sticky table-header fill. */
  tableHeaderBg: string;
  /** The sticky label-gutter / header hairline border. */
  gutterBorder: string;
  /** The sticky gutter's drop shadow. */
  gutterShadow: string;
  /** A positioned block's resting shadow. */
  blockShadow: string;
  /** A cancelled block's (lighter) resting shadow. */
  blockShadowCancelled: string;

  /** Primary block/lane-label text (e.g. bay/staff lane name, customer label). */
  textPrimary: string;
  /** Stronger block text (the Timeline customer label). */
  textStrong: string;
  /** Muted secondary text (hour-axis labels, gutter headers). */
  textMuted: string;
  /** Blue-gray meta text (block time range, secondary meta line). */
  textMeta: string;
  /** The neutral status pill's ink when the status is unrecognized. */
  textNeutralStatus: string;
  /** The neutral status pill's fill. */
  neutralStatusBg: string;
  /** The heatmap AVG-column label. */
  avgLabel: string;
  /** The `event_busy` empty-state glyph. */
  emptyIcon: string;
  /** The decorative bay/staff lane swatch. */
  laneSwatch: string;

  /** Day/Week List row hover fill. */
  rowHoverBg: string;
  /** Day/Week List cancelled-row hover fill. */
  rowHoverBgCancelled: string;
  /** Day/Week List row divider. */
  rowBorder: string;
  /** Day/Week List cancelled-row divider. */
  rowBorderCancelled: string;
  /** Week List sticky day-header border. */
  listHeaderBorder: string;
  /** Week List sticky day-header fill. */
  listHeaderBg: string;
}

const LIGHT: CalendarMaps = {
  servicePalette: [
    { main: '#1565c0', bg: '#e3f2fd' },
    { main: '#2e7d32', bg: '#e8f5e9' },
    { main: '#6a1b9a', bg: '#f3e5f5' },
    { main: '#ef6c00', bg: '#fff3e0' },
    { main: '#00838f', bg: '#e0f7fa' },
    { main: '#c2185b', bg: '#fce4ec' },
  ],
  neutralService: { main: '#90a4ae', bg: '#fafafa' },

  nowLine: '#e53935',
  gridLine: '#eeeeee',
  cancelledFill: '#fafafa',
  offHoursFill: 'rgba(96, 125, 139, 0.05)',
  todayTint: '#e3f2fd',
  todayTrackTint: 'rgba(227,242,253,.4)',
  todayAccent: '#1565c0',
  todayChipInk: '#fff',
  headerInk: '#fff',

  statusPill: {
    confirmed: { fg: '#2e7d32', bg: '#e8f5e9' },
    pending: { fg: '#ef6c00', bg: '#fff3e0' },
    cancelled: { fg: '#c62828', bg: '#ffebee' },
    neutral: { fg: '#546e7a', bg: '#eceff1' },
  },

  // Same values as `statusPill.confirmed`/`.pending`/`.neutral` (DNKADM-0350
  // — same green/amber/grey family, deliberately not a forked palette).
  // `refunded` reuses `comp`'s neutral pair verbatim (Operator ruling — same
  // visual weight, distinct label).
  paymentState: {
    paid: { fg: '#2e7d32', bg: '#e8f5e9' },
    due: { fg: '#ef6c00', bg: '#fff3e0' },
    comp: { fg: '#546e7a', bg: '#eceff1' },
    refunded: { fg: '#546e7a', bg: '#eceff1' },
    // DNKADM-0402 U5: the warning pair, shared with `due` — an unpaid booking is the
    // same kind of front-desk task, said in the server's own word and with its amount.
    unpaid: { fg: '#ef6c00', bg: '#fff3e0' },
    // Operator decision 2026-09-22 — a DISTINCT pair, deliberately not `unpaid`'s amber:
    // "Unpaid" is money to collect, "Payment in progress" is money that may ALREADY have
    // moved, and the two must not look like the same task. Blue reads as in-flight rather
    // than owed, and reuses the calendar map's existing heatmap blue ink (#1565c0 on a
    // pale blue fill = 4.96:1, AA for the badge's bold small text). The WORD still carries
    // the state on its own — nothing here depends on colour.
    'in-progress': { fg: '#1565c0', bg: '#e3f2fd' },
    // DNKADM-0408 U1 — money dink will not settle on its own; a manager must look. The
    // `statusPill.cancelled` red pair verbatim (not a forked palette): it is the one state
    // on the board that is a problem rather than a task.
    'needs-review': { fg: '#c62828', bg: '#ffebee' },
  },

  cancelledBlockText: '#707070',
  cancelledListText: '#767676',

  heatmapLowFg: '#1565c0',
  heatmapMidFg: '#000',
  heatmapHighFg: '#fff',
  heatmapBase: '21, 101, 192',

  // Was '#fff' — an exact copy of the old light AdminTokens surface, so it is
  // bound to the token (DNKADM-0297 U3); the calendar's panels track app Paper.
  surface: LT.surface,
  tableHeaderBg: '#fafafa',
  gutterBorder: '#e0e0e0',
  gutterShadow: '2px 0 4px rgba(0,0,0,.04)',
  blockShadow: '0 1px 1px rgba(0,0,0,.08)',
  blockShadowCancelled: '0 1px 1px rgba(0,0,0,.044)',

  textPrimary: '#37474f',
  textStrong: '#263238',
  textMuted: '#9e9e9e',
  textMeta: '#607d8b',
  textNeutralStatus: '#546e7a',
  neutralStatusBg: '#eceff1',
  avgLabel: '#bdbdbd',
  emptyIcon: '#cfd8dc',
  laneSwatch: '#90a4ae',

  rowHoverBg: '#f5f9ff',
  rowHoverBgCancelled: 'rgba(245, 249, 255, 0.6)',
  rowBorder: '#f0f0f0',
  rowBorderCancelled: 'rgba(240, 240, 240, 0.6)',
  listHeaderBorder: '#e8eaed',
  listHeaderBg: '#f5f7f9',
};

const DARK: CalendarMaps = {
  // Same six families as light, lifted to pastel mains + translucent (family-tinted)
  // fills — the established dark ramp (mirrors `STATUS_DARK` / `TIER_CHIP_DARK`).
  servicePalette: [
    { main: '#9FC0E8', bg: 'rgba(159,192,232,0.22)' },
    { main: '#7FD3A3', bg: 'rgba(127,211,163,0.22)' },
    { main: '#C9BCE8', bg: 'rgba(201,188,232,0.22)' },
    { main: '#E8C27A', bg: 'rgba(232,194,122,0.22)' },
    { main: '#8FD0D4', bg: 'rgba(143,208,212,0.22)' },
    { main: '#F0A0B8', bg: 'rgba(240,160,184,0.22)' },
  ],
  neutralService: { main: '#B8C0BA', bg: 'rgba(184,192,186,0.22)' },

  nowLine: '#FF5252',
  gridLine: DT.line, // 0271 copy of the token — bound (DNKADM-0297 U3)
  // DELIBERATELY INDEPENDENT: the dimmed fill the DNKADM-0181-approved
  // `cancelledBlockText` ink was contrast-rated against (via
  // `dimDecorativeColor`'s 0.55 alpha). Binding it to a moving surface token
  // would silently re-rate that approved pairing — it changes only with a
  // fresh contrast review.
  cancelledFill: '#1E2621',
  offHoursFill: 'rgba(120, 144, 156, 0.08)',
  todayTint: 'rgba(159,192,232,0.14)',
  todayTrackTint: 'rgba(159,192,232,0.08)',
  todayAccent: '#9FC0E8',
  // DELIBERATELY INDEPENDENT: ink ON the todayAccent / service-color FILLS,
  // not a surface — it only coincidentally equaled the old page bg. Coupling
  // ink-on-fill to the page background would be a semantic error.
  todayChipInk: '#0B0F0D',
  headerInk: '#0B0F0D',

  statusPill: {
    confirmed: { fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)' },
    pending: { fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)' },
    cancelled: { fg: '#F0A090', bg: 'rgba(192,73,46,0.18)' },
    neutral: { fg: '#9AA39D', bg: 'rgba(154,163,157,0.16)' },
  },

  // Same values as `statusPill.confirmed`/`.pending`/`.neutral` (DNKADM-0350).
  // `refunded` reuses `comp`'s neutral pair verbatim (Operator ruling).
  paymentState: {
    paid: { fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)' },
    due: { fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)' },
    comp: { fg: '#9AA39D', bg: 'rgba(154,163,157,0.16)' },
    refunded: { fg: '#9AA39D', bg: 'rgba(154,163,157,0.16)' },
    // DNKADM-0402 U5 — the warning pair, shared with `due`.
    unpaid: { fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)' },
    // Operator decision 2026-09-22 — the dark-mode counterpart of the light blue pair,
    // reusing this map's existing `heatmapLowFg` ink over a low-alpha blue fill.
    'in-progress': { fg: '#9FC0E8', bg: 'rgba(21,101,192,0.22)' },
    // DNKADM-0408 U1 — the dark `statusPill.cancelled` pair verbatim.
    'needs-review': { fg: '#F0A090', bg: 'rgba(192,73,46,0.18)' },
  },

  // DELIBERATELY INDEPENDENT: DNKADM-0181-approved contrast values, owned by
  // `contrastPolicy.ts` — they change only with a fresh contrast review, not
  // with the surface ladder (they only coincidentally equaled old tokens).
  cancelledBlockText: '#8F9992',
  cancelledListText: '#79837C',

  heatmapLowFg: '#9FC0E8',
  // DELIBERATELY INDEPENDENT: heatmap label inks over the utilization-alpha
  // CELL fills (DNKADM-0181 policy, `heatmapForeground`) — ink-on-fill, not
  // surface ladder.
  heatmapMidFg: '#0B0F0D',
  heatmapHighFg: '#EFF3F0',
  heatmapBase: '100, 181, 246',

  // 0271 ramp copies of the dark AdminTokens — bound (DNKADM-0297 U3).
  surface: DT.surface,
  tableHeaderBg: DT.surface2,
  gutterBorder: DT.line,
  gutterShadow: '2px 0 4px rgba(0,0,0,0.4)',
  blockShadow: '0 1px 1px rgba(0,0,0,0.35)',
  blockShadowCancelled: '0 1px 1px rgba(0,0,0,0.2)',

  // 0271 ramp copies of the dark AdminTokens text family — bound (0297 U3).
  // (Light keeps the calendar's own blue-grey text literals, which never
  // matched a token.)
  textPrimary: DT.text,
  textStrong: DT.text,
  textMuted: DT.text2,
  textMeta: '#9EB0BB',
  // DELIBERATELY INDEPENDENT: status-pill inks/fills (the neutral pill's fg +
  // its rgba fill derive from the OLD text2 by design of the pill family) —
  // U3's rule: pill fg never changes, bg alpha only on a measured failure,
  // and the 0297 re-measure showed none (see the delivery).
  textNeutralStatus: '#9AA39D',
  neutralStatusBg: 'rgba(154,163,157,0.16)',
  // DNKADM-0330: the 10px empty-load label also renders inside the translucent
  // today header. This minimally lifted neutral keeps that actual text pair at
  // 4.8+:1 without changing the light map or the global text ladder.
  avgLabel: '#989F9A',
  emptyIcon: DT.lineStrong, // 0271 ramp copy — bound (0297 U3)
  laneSwatch: '#8798A0',

  rowHoverBg: 'rgba(159,192,232,0.08)',
  rowHoverBgCancelled: 'rgba(159,192,232,0.04)',
  rowBorder: DT.lineStrong,
  rowBorderCancelled: 'rgba(30,38,32,0.6)',
  listHeaderBorder: DT.lineSoft, // 0271 ramp copy — bound (0297 U3)
  listHeaderBg: DT.surface2, // 0271 ramp copy — bound (0297 U3)
};

/** Per-mode calendar-domain map. Light === the pre-0271 literals. */
export function buildCalendarMaps(mode: 'light' | 'dark'): CalendarMaps {
  return mode === 'dark' ? DARK : LIGHT;
}

/** Resolve the calendar-domain map off the mounted theme. */
export function resolveCalendarMaps(theme: Theme): CalendarMaps {
  return buildCalendarMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
}
